import { Dtos } from "@inrev/common";
import { FormField, FormItemLabel, FormSection } from "@portal/components/form";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { EntityCard } from "@portal/components/multiform/components/EntityCard";
import { Icon } from "@portal/components/primitives";
import { Button } from "@portal/components/ui/Button";
import { Checkbox } from "@portal/components/ui/Checkbox";
import { InlineLink } from "@portal/components/ui/InlineLink";
import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { useState } from "react";
import { type FieldPath, useFormContext, useFormState, useWatch } from "react-hook-form";
import { getNestedValue, isEmpty, isFieldPartial } from "../../../../../../utils/schema-fields";
import { AccountExposureSizeSelect } from "../../../shared/AccountExposureSizeSelect";
import { AccountCompanyModal } from "../../components/AccountCompanyModal";
import { AccountIndividualModal } from "../../components/AccountIndividualModal";
import { useAccountIndividuals } from "../../hooks/useAccountIndividuals";
import { PaymentAndPerformanceBondsField } from "../../PaymentAndPerformanceBondsField";
import type { AccountFormValues, CompanyFormData, OwnerFormData } from "../../schema";

export const DetailsStep = () => {
	const { control } = useFormContext<AccountFormValues>();

	return (
		<div className="w-full min-h-fit flex flex-col space-y-[60px] pt-2 pb-[125px]">
			<CompaniesSection />
			<IndividualsSection />
			<PaymentAndPerformanceBondsField deselectable />
			<FormField className="space-y-[24px]">
				<FormItemLabel className="pb-[5px]" name="account.modelSize">
					Which best describes the total bonding need for all companies in this account over the
					next 12 months?
				</FormItemLabel>
				<AccountExposureSizeSelect name={"account.modelSize"} control={control} />
			</FormField>
		</div>
	);
};

const CompaniesSection = () => {
	const {
		control,
		getValues,
		setValue,
		formState: { errors, isSubmitted },
	} = useFormContext<AccountFormValues>();
	const companies = (useWatch({ control, name: "account.details.companies" }) ??
		[]) as CompanyFormData[];
	const [editingIndex, setEditingIndex] = useState<number | null>(null);

	const followRenamedPrincipal = (previousName: string, name: string) => {
		if (previousName === name || getValues("quote.principal.name") !== previousName) return;
		setValue("quote.principal.name", name, { shouldValidate: isSubmitted });
	};

	const save = (company: CompanyFormData) => {
		const isEditing = editingIndex !== null && editingIndex >= 0;
		let updated = [...companies];
		if (isEditing) {
			updated[editingIndex] = company;
		} else {
			updated.push(company);
		}
		if (company.isPrimaryCompany) {
			updated = updated.map((c) => (c === company ? c : { ...c, isPrimaryCompany: false }));
		}
		setValue("account.details.companies", updated, { shouldValidate: true, shouldDirty: true });
		if (isEditing) followRenamedPrincipal(companies[editingIndex].name, company.name);
		setEditingIndex(null);
	};

	return (
		<FormSection
			topPadding
			header="Companies"
			name="account.details.companies"
			subHeader={
				errors?.account?.details?.companies?.root?.message ??
				"Please list all companies in this account"
			}
			subHeaderClassName={errors?.account?.details?.companies ? "text-danger" : ""}
		>
			<div className="flex flex-col space-y-2">
				{companies.map((company, index) => {
					return (
						<EntityCard
							key={company.name || `new-${index}`}
							title={company.name || "Untitled Company"}
							subtitle={company.isPrimaryCompany ? "Primary" : undefined}
							schema={Dtos.Company.Create.Request.schema}
							entity={company}
							icon={<Icon type="building-office-alt" size="small" color="secondary" />}
							onEdit={() => setEditingIndex(index)}
							onRemove={() => {
								const updated = companies.filter((_, i) => i !== index);
								setValue("account.details.companies", updated, {
									shouldValidate: true,
								});
							}}
							condensed
						/>
					);
				})}
				<Button
					onClick={() => setEditingIndex(-1)}
					color="inverse"
					filled
					thinFont
					className="ml-[-4px] w-fit h-[34px]"
				>
					<div className="flex min-w-fit items-center space-x-[6px]">
						<Icon type="plus" size="mini" />
						<div className="min-w-fit text-[13.5px] font-medium">Add Company</div>
					</div>
				</Button>
			</div>
			<AccountCompanyModal
				isOpen={editingIndex !== null}
				onClose={() => setEditingIndex(null)}
				onSubmit={save}
				initialData={
					editingIndex !== null && editingIndex >= 0
						? companies[editingIndex]
						: companies.length === 0
							? ({ isPrimaryCompany: true } as CompanyFormData)
							: ({ isPrimaryCompany: false } as CompanyFormData)
				}
				existingNames={companies
					.filter((_, i) => i !== editingIndex)
					.map((c) => c.name)
					.filter(Boolean)}
			/>
		</FormSection>
	);
};

const CreditPermissionCheckbox = ({
	owner,
	tupleIndex,
	position,
	setCreditPermission,
	showError,
}: {
	owner: OwnerFormData;
	tupleIndex: number;
	position: number;
	setCreditPermission: (
		groupIndex: number,
		memberIndex: number,
		value: boolean | undefined,
	) => void;
	showError: boolean;
}) => {
	const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
	if (!owner.firstName || !owner.lastName) return null;

	return (
		<div className="flex flex-col space-y-[10px] ml-[10px]">
			<div className="flex space-x-[10px]">
				<ConfirmationModalClickWrapper
					shouldConfirm={!owner.creditReportPermission}
					onConfirm={() => setCreditPermission(tupleIndex, position, true)}
					subMessage={
						<div className="max-w-[450px] h-full flex space-x-[15px] basis-0">
							<div className="w-[8px] min-h-full rounded bg-bc-default" />
							<div className="text-[14px] font-normal text-secondary leading-[23px] text-left">
								You understand that by checking this box,{" "}
								<span className="font-semibold">{name}</span> is providing "written instructions" to
								InRev under the
								<br />
								<InlineLink
									to="https://www.ftc.gov/legal-library/browse/statutes/fair-credit-reporting-act"
									newTab
								>
									Fair Credit Reporting Act
								</InlineLink>{" "}
								authorizing InRev to obtain personal credit report information or other information
								from Equifax.
								<br />
								<span className="font-semibold">{name}</span> authorizes inRev to obtain personal
								credit report information and other information from Equifax.
							</div>
						</div>
					}
					confirmButtonText="I Understand"
					cancelButton={false}
				>
					<Checkbox
						className="mt-[1px]"
						checked={owner.creditReportPermission === true}
						onChange={() => {
							if (owner.creditReportPermission === true) {
								setCreditPermission(tupleIndex, position, undefined);
							}
						}}
					/>
				</ConfirmationModalClickWrapper>
				<div className="text-[13px] text-secondary">
					<span className="font-medium text-secondary">{name}</span> authorizes inRev to obtain
					personal credit report information and other information from Equifax.
				</div>
			</div>
			{!owner.creditReportPermission && showError && (
				<div className="text-xs text-danger !mt-[5px]">Required</div>
			)}
		</div>
	);
};

const IndividualsSection = () => {
	const { control, getFieldState, getValues, formState } = useFormContext<AccountFormValues>();
	const { isSubmitted } = useFormState({ control });
	const schema = useFormSchema();

	const creditPermissionHasError = (tupleIdx: number, pos: number): boolean =>
		getFieldState(
			`account.details.individuals.${tupleIdx}.${pos}.creditReportPermission` as FieldPath<AccountFormValues>,
			formState,
		).invalid;
	const showPartial =
		isSubmitted &&
		!!schema &&
		isFieldPartial(schema, "account.details.individuals") &&
		isEmpty(getNestedValue(getValues(), "account.details.individuals"));
	const {
		entries,
		isOpen,
		editingOwner,
		editingSpouse,
		addOwner,
		editOwner,
		editSpouseOwner,
		removeIndividual,
		saveOwnerAndSpouse,
		setCreditPermission,
		close,
	} = useAccountIndividuals();

	return (
		<FormSection
			topPadding
			header="Individual Owners"
			subHeader="Please list all individuals that own at least 20% of one or more companies in this account"
			partial={showPartial}
		>
			<div className="flex flex-col space-y-2">
				{entries.map(({ owner, spouse, index }) => {
					const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ");
					const spouseIsAlsoOwner = spouse && "type" in spouse && spouse.type === "owner";
					const spouseName =
						spouse && !spouseIsAlsoOwner
							? [spouse.firstName, spouse.lastName].filter(Boolean).join(" ")
							: undefined;
					return (
						<div key={`individual-${index}`} className="flex flex-col space-y-2">
							<EntityCard
								title={name || "Untitled Individual"}
								subtitle={spouseName ? `& ${spouseName}` : undefined}
								entity={owner}
								schema={Dtos.Individual.Create.Request.ownerCardSchema}
								icon={<Icon type="user" size="small" color="secondary" />}
								onEdit={() => editOwner(index)}
								onRemove={() => removeIndividual(index, 0)}
								condensed
							>
								<CreditPermissionCheckbox
									owner={owner}
									tupleIndex={index}
									position={0}
									setCreditPermission={setCreditPermission}
									showError={isSubmitted || creditPermissionHasError(index, 0)}
								/>
							</EntityCard>
							{spouseIsAlsoOwner && (
								<EntityCard
									title={
										[spouse.firstName, spouse.lastName].filter(Boolean).join(" ") ||
										"Untitled Individual"
									}
									entity={spouse}
									schema={Dtos.Individual.Create.Request.ownerCardSchema}
									icon={<Icon type="user" size="small" color="secondary" />}
									onEdit={() => editSpouseOwner(index)}
									onRemove={() => removeIndividual(index, 1)}
									condensed
								>
									<CreditPermissionCheckbox
										owner={spouse as OwnerFormData}
										tupleIndex={index}
										position={1}
										setCreditPermission={setCreditPermission}
										showError={isSubmitted || creditPermissionHasError(index, 1)}
									/>
								</EntityCard>
							)}
						</div>
					);
				})}
				<Button
					onClick={addOwner}
					color="inverse"
					filled
					thinFont
					className="ml-[-4px] w-fit h-[34px]"
				>
					<div className="flex min-w-fit items-center space-x-[6px]">
						<Icon type="plus" size="mini" />
						<div className="min-w-fit text-[13.5px] font-medium">Add Individual</div>
					</div>
				</Button>
			</div>
			<AccountIndividualModal
				isOpen={isOpen}
				onClose={close}
				onSave={saveOwnerAndSpouse}
				initialOwner={editingOwner}
				initialSpouse={editingSpouse}
			/>
		</FormSection>
	);
};
